import React, { createContext, useContext, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import {
  setPerformanceInfo,
  performanceSelectors,
} from "../redux/slices/performance.slice";
import { useDevicePerformance } from "../hooks/useDevicePerformance";
import { debugLog, DEBUG_CATEGORIES } from "../utils/debug";

interface PerformanceContextType {
  shouldDisableAnimations: boolean;
  performanceScore: number;
  isLowEndDevice: boolean;
  detectionComplete: boolean;
}

const PerformanceContext = createContext<PerformanceContextType | undefined>(
  undefined
);

export const PerformanceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const devicePerformance = useDevicePerformance();

  // Selectors do Redux
  const shouldDisableAnimations = useAppSelector(
    performanceSelectors.shouldDisableAnimations
  );
  const performanceScore = useAppSelector(
    performanceSelectors.performanceScore
  );
  const isLowEndDevice = useAppSelector(performanceSelectors.isLowEndDevice);
  const detectionComplete = useAppSelector(
    performanceSelectors.detectionComplete
  );

  // Quando a detecção estiver completa, atualizar Redux
  useEffect(() => {
    if (devicePerformance.detectionComplete) {
      debugLog(DEBUG_CATEGORIES.PERF, "Detecção completa, atualizando Redux");
      dispatch(
        setPerformanceInfo({
          isLowEnd: devicePerformance.isLowEnd,
          prefersReducedMotion: devicePerformance.prefersReducedMotion,
          performanceScore: devicePerformance.performanceScore,
          metrics: devicePerformance.metrics,
        })
      );
    }
  }, [devicePerformance.detectionComplete, dispatch, devicePerformance]);

  // Sincronizar mudanças de preferência e overrides sem reaplicar a classe
  // quando o estado efetivo permanece igual.
  useEffect(() => {
    if (!detectionComplete) return;
    document.documentElement.classList.toggle(
      "reduced-animations",
      shouldDisableAnimations
    );
  }, [detectionComplete, shouldDisableAnimations]);

  // [FLICKERING_PREVENTION] Memoizar value com dependências estáveis para evitar re-renders
  const value = React.useMemo<PerformanceContextType>(
    () => ({
      shouldDisableAnimations,
      performanceScore,
      isLowEndDevice,
      detectionComplete,
    }),
    [shouldDisableAnimations, performanceScore, isLowEndDevice, detectionComplete]
  );

  return (
    <PerformanceContext.Provider value={value}>
      {children}
    </PerformanceContext.Provider>
  );
};

export const usePerformanceContext = (): PerformanceContextType => {
  const context = useContext(PerformanceContext);
  if (context === undefined) {
    throw new Error(
      "usePerformanceContext deve ser usado dentro de um PerformanceProvider"
    );
  }
  return context;
};
